/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/BuyerCompaniesTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./BuyerCompaniesTable.module.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ReactComponent as EditIcon } from "../../assets/icons/BuyerCompaniesTable/EditIcon.svg";
import AssetImage from "../AssetImage";

const buyerCompanyRowKey = (buyerCompany) =>
  buyerCompany._id || buyerCompany.id || buyerCompany.RFC;

const BuyerCompaniesTable = ({ buyerCompanies, onSort, renderArrow }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleEdit = (company) => {
    navigate("/buyerCompanyForm", {
      state: {
        isEditMode: true,
        companyData: company,
      },
    });
  };

  return (
    <div className={styles.buyerCompaniesTableContainer}>
      <table className={styles.buyerCompaniesTable}>
        <thead>
          <tr>
            <th onClick={onSort}>
              <div className={styles.sortableHeader}>
                {t("components.buyerCompaniesTable.companyName")}{" "}
                {renderArrow()}
              </div>
            </th>
            <th>{t("components.buyerCompaniesTable.socialReason")}</th>
            <th>{t("components.buyerCompaniesTable.RFC")}</th>
            <th>{t("components.buyerCompaniesTable.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {buyerCompanies.map((buyerCompany) => (
            <tr key={buyerCompanyRowKey(buyerCompany)}>
              <td>
                <div className={styles.companyCell}>
                  {buyerCompany.logo && (
                    <AssetImage
                      src={buyerCompany.logo}
                      alt={t("common.companyLogoFor", {
                        name: buyerCompany.nombreComercial,
                      })}
                      className={styles.companyLogo}
                    />
                  )}
                  <span className={styles.companyName}>
                    {buyerCompany.nombreComercial}
                  </span>
                </div>
              </td>
              <td>{buyerCompany.razonSocial}</td>
              <td>{buyerCompany.RFC}</td>
              <td className={styles.actionsCell}>
                <button
                  onClick={() => handleEdit(buyerCompany)}
                  className={styles.actionButton}
                >
                  <EditIcon />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BuyerCompaniesTable;
